import { initializeColourOverflow, updateColourOverflows, cleanupColourOverflow } from './raith-swatch-overflow.js';
export function registerRaithCollection(win = window) {
  if (!win.customElements.get('raith-card-gallery')) {
    // Read once before custom-element upgrades start mutating each card.
    // Reuse the same snapshot across all gallery resize handlers too.
    let galleryViewportWidth = win.innerWidth;
    win.addEventListener('resize', () => { galleryViewportWidth = win.innerWidth; }, {passive: true});
    class RaithCardGallery extends win.HTMLElement {
      connectedCallback() {
        this.disconnectedCallback();
        this.controller = new win.AbortController();
        const { signal } = this.controller;
        this.track = this.querySelector('[data-card-gallery-track]');
        if (!this.track) return;
        this.slides = [...this.track.children];
        this.buttons = [...this.querySelectorAll('[data-card-gallery-step]')];
        this.status = this.querySelector('[data-card-gallery-status]');
        this.progress = this.querySelector('[data-card-gallery-progress]');
        this.index = 0;
        this.viewportWidth = galleryViewportWidth;
        this.nearViewport = !win.IntersectionObserver;
        if (win.IntersectionObserver) {
          this.observer = new win.IntersectionObserver(entries => {
            this.nearViewport = entries.some(entry => entry.isIntersecting);
            if (this.nearViewport) this.goTo(this.index, false);
            else this.syncImages();
          }, {rootMargin: '600px 0px'});
          this.observer.observe(this);
        }
        const prepareImages = () => {
          if (this.nearViewport) return;
          this.nearViewport = true;
          this.goTo(this.index, false);
        };
        this.addEventListener('focusin', prepareImages, {signal});
        this.addEventListener('pointerdown', prepareImages, {passive: true, signal});
        if (this.slides.length > 1) {
          this.track.tabIndex = 0;
          this.track.setAttribute('aria-label', this.getAttribute('aria-label'));
          for (const button of this.buttons) {
            button.hidden = false;
            button.addEventListener('click', () => this.goTo(this.index + Number(button.dataset.cardGalleryStep)), {signal});
          }
          this.track.addEventListener('scroll', () => {
            this.needsScrollReset = true;
            if (this.scrollFrame) return;
            this.scrollFrame = win.requestAnimationFrame(() => { this.scrollFrame = null; this.update(); });
          }, {passive: true, signal});
          this.track.addEventListener('keydown', event => {
            const rtl = win.getComputedStyle(this.track).direction === 'rtl';
            const delta = event.key === 'ArrowRight' ? (rtl ? -1 : 1) : event.key === 'ArrowLeft' ? (rtl ? 1 : -1) : 0;
            if (!delta && event.key !== 'Home' && event.key !== 'End') return;
            event.preventDefault();
            this.goTo(event.key === 'Home' ? 0 : event.key === 'End' ? this.slides.length - 1 : this.index + delta);
          }, {signal});
          this.track.addEventListener('pointerdown', event => {
            this.gesture = {x: event.clientX, y: event.clientY, scroll: this.track.scrollLeft, moved: false};
          }, {passive: true, signal});
          this.track.addEventListener('pointermove', event => {
            if (!this.gesture) return;
            const x = Math.abs(event.clientX - this.gesture.x), y = Math.abs(event.clientY - this.gesture.y);
            if (x > 10 && x > y) this.gesture.moved = true;
          }, {passive: true, signal});
          const finishGesture = () => {
            if (this.gesture && (this.gesture.moved || Math.abs(this.track.scrollLeft - this.gesture.scroll) > 5)) this.suppressClickUntil = Date.now() + 500;
            this.gesture = null;
          };
          this.track.addEventListener('pointerup', finishGesture, {passive: true, signal});
          this.track.addEventListener('pointercancel', finishGesture, {passive: true, signal});
          this.track.addEventListener('click', event => {
            if (event.detail && Date.now() < (this.suppressClickUntil || 0)) { event.preventDefault(); event.stopPropagation(); }
          }, {capture: true, signal});
        }
        win.addEventListener('resize', () => {
          if (this.viewportWidth === galleryViewportWidth || this.resizeFrame) return;
          this.resizeFrame = win.requestAnimationFrame(() => {
            this.resizeFrame = null;
            if (this.viewportWidth === galleryViewportWidth) return;
            this.viewportWidth = galleryViewportWidth;
            this.goTo(this.index, false);
          });
        }, {passive: true, signal});
        this.frame = win.requestAnimationFrame(() => this.setView(this.closest('raith-collection')?.dataset.view || 'model'));
        this.update();
      }
      disconnectedCallback() {
        this.controller?.abort(); this.observer?.disconnect();
        for (const key of ['frame', 'scrollFrame', 'resizeFrame']) {
          if (this[key]) win.cancelAnimationFrame(this[key]);
          this[key] = null;
        }
        this.nearViewport = false; this.syncImages(); this.gesture = null;
      }
      syncImages() {
        const active = !this.closest('[data-collection-responsive-result]') && this.nearViewport && win.matchMedia('(max-width: 900px)').matches;
        this.toggleAttribute('data-gallery-active', active);
        for (const [index, slide] of (this.slides || []).entries()) {
          const template = slide.querySelector('template[data-card-gallery-image]');
          if (!template) continue;
          const image = slide.querySelector('img');
          if (active && Math.abs(index - this.index) <= 1) {
            if (!image) slide.append(template.content.cloneNode(true));
          } else image?.remove();
        }
      }
      setView(view) {
        if (!this.slides) return;
        const productIndex = view === 'product' ? this.slides.findIndex(slide => slide.dataset.imageSource === this.dataset.productImage) : 0;
        this.goTo(Math.max(0, productIndex), false);
      }
      goTo(index, smooth = true) {
        if (!this.track || !this.slides.length) return;
        this.index = Math.max(0, Math.min(index, this.slides.length - 1));
        this.syncImages();
        if (!this.hasAttribute('data-gallery-active')) { this.updateButtons(); return; }
        // New model galleries already start at zero. Even a no-op scrollTo
        // forces the browser to lay out their newly hydrated neighbouring image.
        if (!this.index && !this.needsScrollReset) { this.updateButtons(); return; }
        // The first slide is the scroll origin in both LTR and RTL. Initial
        // hydration must not force layout for every card just to return to it.
        let left = 0;
        if (this.index) {
          const track = this.track.getBoundingClientRect(), slide = this.slides[this.index].getBoundingClientRect();
          const rtl = win.getComputedStyle(this.track).direction === 'rtl';
          left = this.track.scrollLeft + (rtl ? slide.right - track.right : slide.left - track.left);
        }
        this.track.scrollTo({left, behavior: smooth && !win.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'smooth' : 'instant'});
        this.needsScrollReset = this.index !== 0;
        this.updateButtons();
      }
      update() {
        if (!this.hasAttribute('data-gallery-active') || !this.track.clientWidth) return;
        const bounds = this.track.getBoundingClientRect(), rtl = win.getComputedStyle(this.track).direction === 'rtl';
        let nearest = Infinity;
        this.slides.forEach((slide, index) => {
          const box = slide.getBoundingClientRect(), distance = Math.abs(rtl ? bounds.right - box.right : box.left - bounds.left);
          if (distance < nearest) { nearest = distance; this.index = index; }
        });
        this.syncImages();
        this.updateButtons();
      }
      updateButtons() {
        this.dataset.galleryIndex = String(this.index);
        if (this.progress && this.progress.style.getPropertyValue('--vl-gallery-progress-index') !== String(this.index)) this.progress.style.setProperty('--vl-gallery-progress-index', String(this.index));
        for (const button of this.buttons) button.disabled = Number(button.dataset.cardGalleryStep) < 0 ? this.index === 0 : this.index === this.slides.length - 1;
        const label = this.slides[this.index]?.dataset.imageLabel || '';
        if (this.status && this.status.textContent !== label) this.status.textContent = label;
      }
    }
    win.customElements.define('raith-card-gallery', RaithCardGallery);
  }
  if (win.customElements.get('raith-collection')) return;
  const preferences = new Map();
  class RaithCollection extends win.HTMLElement {
    connectedCallback() {
      this.cleanup();
      this.controller = new win.AbortController();
      const { signal } = this.controller;
      this.stories = [...this.querySelectorAll('[data-collection-story]')];
      this.cards = [...this.querySelectorAll('.vl-collection__card')].filter(card => card.closest('raith-collection') === this);
      this.switcher = this.querySelector('[data-collection-switch]');
      this.preferenceKey = `${this.dataset.sectionId}:${this.dataset.collectionId}`;
      this.syncViewCapability();
      this.initializeFamilies();
      this.initializeStoryFamilies();
      this.updateColourOverflows([...this.families.values(), ...this.storyFamilies.values()]);
      this.addEventListener('click', event => {
        const storyChoice = event.target.closest('[data-story-colour]');
        if (storyChoice) {
          const story = storyChoice.closest('[data-collection-story]'), state = this.storyFamilies.get(story);
          if (state && state.choices.includes(storyChoice) && storyChoice.parentElement === state.controls) this.selectStoryMember(story, state, storyChoice.dataset.storyColour);
          return;
        }
        const choice = event.target.closest('[data-collection-colour]');
        if (choice) {
          const card = choice.closest('.vl-collection__card'), state = this.families.get(card);
          if (state && state.choices.includes(choice) && choice.parentElement === state.controls && state.controls.parentElement === card && state.display.parentElement === card) {
            this.selectMember(card, state, choice.dataset.collectionColour);
          }
          return;
        }
        const button = event.target.closest('[data-collection-view]');
        if (!button || !this.switcher?.contains(button)) return;
        const view = button.dataset.collectionView;
        if (view !== 'model' && view !== 'product') return;
        preferences.set(this.preferenceKey, view);
        this.setView(view);
      }, { signal });
      win.document.addEventListener('shopify:section:load', () => this.updateColourOverflows([...this.families.values(), ...this.storyFamilies.values()]), { signal });
      win.document.addEventListener('shopify:section:unload', () => win.requestAnimationFrame(() => { if (this.isConnected) this.updateColourOverflows([...this.families.values(), ...this.storyFamilies.values()]); }), { signal });
      this.viewportWidth = win.innerWidth;
      win.addEventListener('resize', () => {
        if (this.viewportWidth === win.innerWidth || this.resizeFrame) return;
        this.resizeFrame = win.requestAnimationFrame(() => {
          this.resizeFrame = null;
          if (this.viewportWidth === win.innerWidth) return;
          this.viewportWidth = win.innerWidth;
          this.syncViewCapability();
          this.setView(preferences.get(this.preferenceKey) || 'model');
          this.updateColourOverflows([...this.families.values(), ...this.storyFamilies.values()]);
        });
      }, { passive: true, signal });
      this.setView(preferences.get(this.preferenceKey) || 'model');
      if (this.dataset.priceSortModule) {
        this.setPriceSortStatus(this.dataset.priceSortState || 'native');
        if (this.dataset.priceSortState === 'pending') this.initializePriceSort();
      }
    }
    setPriceSortStatus(state, url) {
      this.dataset.priceSortState = state;
      // A native drawer moves to body while open. Do not let it open against a
      // pending initial root that is about to be replaced by the sorted result.
      for (const toggle of this.querySelectorAll('[data-raith-filter-toggle]')) toggle.disabled = state === 'pending';
      for (const sidebar of this.querySelectorAll('raith-filter-sidebar')) sidebar.toggleAttribute('inert', state === 'pending');
      const status = this.querySelector('[data-price-sort-status]');
      if (!status) return;
      status.hidden = state === 'ready' || state === 'native';
      status.querySelector('[data-price-sort-loading]')?.toggleAttribute('hidden', state !== 'pending');
      status.querySelector('[data-price-sort-error]')?.toggleAttribute('hidden', state !== 'error');
      const retry = status.querySelector('[data-price-sort-retry]');
      retry?.toggleAttribute('hidden', state !== 'error');
      if (retry && url) {
        const target = new URL(url, win.location.href);
        target.searchParams.delete('section_id'); target.searchParams.delete('sections');
        retry.querySelector('a').href = target.toString();
      }
    }
    async initializePriceSort() {
      this.priceSortController?.abort();
      const controller = new win.AbortController(), url = new URL(win.location.href);
      this.priceSortController = controller;
      const current = () => this.isConnected && this.priceSortController === controller && !controller.signal.aborted;
      this.setPriceSortStatus('pending', url);
      try {
        const module = await import(this.dataset.priceSortModule);
        if (!current()) return;
        const template = win.document.createElement('template');
        template.content.append(this.cloneNode(true));
        const prepared = template.content.firstElementChild;
        await module.preparePriceSortedSection(prepared, { url, signal: controller.signal, isCurrent: current, expectedRoot: this, win });
        if (current()) this.replaceWith(win.document.importNode(prepared, true));
      } catch (error) {
        if (current()) this.setPriceSortStatus('error', url);
      }
    }
    disconnectedCallback() { this.cleanup(); }
    cleanup() {
      this.priceSortController?.abort();
      this.controller?.abort();
      if (this.resizeFrame) win.cancelAnimationFrame(this.resizeFrame);
      this.resizeFrame = null;
      for (const [card, state] of this.families || []) {
        cleanupColourOverflow(state);
        if (state.display.parentElement === card) this.selectMember(card, state, card.dataset.collectionResult);
        state.controls.hidden = true;
      }
      this.families = new Map();
      for (const [story, state] of this.storyFamilies || []) {
        cleanupColourOverflow(state);
        this.selectStoryMember(story, state, story.dataset.collectionResult, true);
        state.controls.hidden = true;
        story.append(state.controls);
        const status = story.querySelector('[data-story-status]');
        if (status) status.textContent = '';
      }
      this.storyFamilies = new Map();
    }
    storySource(template, id) {
      const panel = template?.content?.firstElementChild;
      return template?.tagName === 'TEMPLATE' && template.content.children.length === 1 && panel?.dataset.storyMember === id &&
        panel.hasAttribute('data-story-content') && ['2', '3'].includes(panel.dataset.storyLayout) ? panel : null;
    }
    initializeStoryFamilies() {
      for (const story of this.stories) {
        const children = [...story.children];
        const display = children.find(node => node.hasAttribute('data-story-content'));
        const controls = children.find(node => node.hasAttribute('data-story-colours'));
        const registry = children.find(node => node.hasAttribute('data-story-sources'));
        if (!display || !controls || !registry) continue;
        const sources = new Map();
        let valid = true;
        for (const template of registry.children) {
          const id = template.dataset.storySource, panel = this.storySource(template, id);
          if (!panel || !/^[1-9][0-9]*$/.test(id) || sources.has(id)) { valid = false; break; }
          sources.set(id, {template, panel});
        }
        const choices = [...controls.children], baseline = story.dataset.collectionResult;
        if (!valid || display.dataset.storyMember !== baseline || !sources.has(baseline) || choices.length < 2 ||
          new Set(choices.map(choice => choice.dataset.storyColour)).size !== choices.length ||
          !choices.some(choice => choice.dataset.storyColour === baseline) ||
          choices.some(choice => choice.tagName !== 'BUTTON' || choice.type !== 'button' || !sources.has(choice.dataset.storyColour))) continue;
        const copy = display.querySelector('.vl-collection__story-copy');
        if (!copy) continue;
        const state = {display, controls, registry, sources, choices, selected: baseline, baseline};
        this.storyFamilies.set(story, state);
        copy.append(controls); controls.hidden = false;
        this.initializeColourOverflow(state);
      }
    }
    selectStoryMember(story, state, id, restoring = false) {
      const source = state.sources.get(id);
      if (!source || state.selected === id) return;
      if (!restoring && (state.registry.parentElement !== story || [...state.sources].some(([key, item]) =>
        item.template.parentElement !== state.registry || this.storySource(item.template, key) !== item.panel))) {
        this.selectStoryMember(story, state, state.baseline, true); state.controls.hidden = true; this.storyFamilies.delete(story); return;
      }
      const panel = source.panel.cloneNode(true), copy = panel.querySelector('.vl-collection__story-copy');
      if (!copy || state.display.parentElement !== story) return;
      const focused = state.controls.contains(win.document.activeElement) ? win.document.activeElement : null;
      copy.append(state.controls);
      state.display.replaceWith(panel); state.display = panel; state.selected = id;
      story.classList.toggle('vl-collection__story--2', panel.dataset.storyLayout === '2');
      story.classList.toggle('vl-collection__story--3', panel.dataset.storyLayout === '3');
      for (const choice of state.choices) choice.setAttribute('aria-pressed', String(choice.dataset.storyColour === id));
      for (const track of panel.querySelectorAll('[data-collection-track]')) {
        track.hidden = track.dataset.collectionTrack !== (this.dataset.view || 'model'); track.scrollLeft = 0;
      }
      if (!restoring) {
        const status = story.querySelector('[data-story-status]');
        if (status) status.textContent = copy.querySelector('h2')?.textContent || '';
        focused?.focus({preventScroll: true});
        this.updateColourOverflow(state);
      }
    }
    memberPanel(template, id) {
      const panel = template?.content?.firstElementChild;
      return /^[1-9][0-9]*$/.test(id) && template.tagName === 'TEMPLATE' && template.content.children.length === 1 &&
        panel?.hasAttribute('data-collection-card-content') && panel.dataset.memberId === id ? panel : null;
    }
    validRegistry() {
      return this.registry?.parentElement === this && [...this.children].filter(node => node.hasAttribute('data-collection-members')).length === 1 &&
        this.registry.children.length === this.registrySources.size && [...this.registrySources].every(([id, source]) =>
          source.template.parentElement === this.registry && source.template.dataset.collectionMember === id && this.memberPanel(source.template, id) === source.panel) &&
        this.baselineSources.every(({id, card, template, panel}) => card.closest('raith-collection') === this && card.dataset.collectionResult === id &&
          template.parentElement === card && [...card.children].filter(node => node.hasAttribute('data-collection-baseline')).length === 1 &&
          template.dataset.collectionBaseline === id && this.memberPanel(template, id) === panel);
    }
    initializeFamilies() {
      for (const card of this.cards) for (const child of card.children) if (child.hasAttribute('data-collection-colours')) child.hidden = true;
      const registries = [...this.children].filter(node => node.hasAttribute('data-collection-members'));
      if (registries.length !== 1) return;
      this.registry = registries[0]; this.memberSources = new Map(); this.registrySources = new Map(); this.baselineSources = [];
      for (const template of this.registry.children) {
        const id = template.dataset.collectionMember, panel = this.memberPanel(template, id);
        if (!panel || this.memberSources.has(id)) return;
        const source = {template, panel};
        this.memberSources.set(id, source); this.registrySources.set(id, source);
      }
      if (this.registry.hasAttribute('data-collection-shared-baselines')) {
        for (const card of this.cards) {
          const baselines = [...card.children].filter(node => node.hasAttribute('data-collection-baseline'));
          if (!baselines.length) continue;
          const id = card.dataset.collectionResult, template = baselines[0], panel = this.memberPanel(template, id);
          if (baselines.length !== 1 || template.dataset.collectionBaseline !== id || !panel || this.registrySources.has(id)) return;
          const existing = this.memberSources.get(id);
          if (existing && !existing.panel.isEqualNode(panel)) return;
          this.baselineSources.push({id, card, template, panel});
          if (!existing) this.memberSources.set(id, {template, panel});
        }
      }
      if (!this.validRegistry()) return;
      for (const card of this.cards) {
        const children = [...card.children];
        const displays = children.filter(node => node.hasAttribute('data-collection-card-content'));
        const controls = children.filter(node => node.hasAttribute('data-collection-colours'));
        const baselines = children.filter(node => node.hasAttribute('data-collection-baseline'));
        const baseline = card.dataset.collectionResult;
        if (displays.length !== 1 || controls.length !== 1 || baselines.length !== 1 || baselines[0].dataset.collectionBaseline !== baseline) continue;
        const original = this.memberPanel(baselines[0], baseline), choices = [...controls[0].children];
        const ids = choices.map(choice => choice.dataset.collectionColour);
        if (!original || displays[0].dataset.memberId !== baseline || choices.length < 2 || new Set(ids).size !== ids.length || !ids.includes(baseline) ||
            choices.some(choice => choice.tagName !== 'BUTTON' || choice.type !== 'button' || (choice.dataset.collectionColour !== baseline && !this.memberSources.has(choice.dataset.collectionColour)))) continue;
        const state = {display: displays[0], controls: controls[0], choices, baseline, original, allowed: new Set(ids), selected: baseline};
        this.families.set(card, state);
        for (const choice of choices) choice.setAttribute('aria-pressed', String(choice.dataset.collectionColour === baseline));
        state.controls.hidden = false;
        this.initializeColourOverflow(state);
      }
    }
    initializeColourOverflow(state) {
      initializeColourOverflow(state, win, this.controller.signal);
    }
    updateColourOverflow(state) {
      updateColourOverflows([state], win);
    }
    updateColourOverflows(families) {
      updateColourOverflows(families, win);
    }
    selectMember(card, state, id) {
      if (!state.allowed.has(id)) return;
      if (id !== state.baseline && !this.validRegistry()) {
        this.selectMember(card, state, state.baseline); state.controls.hidden = true; this.families.delete(card); return;
      }
      if (state.selected === id) return;
      const source = id === state.baseline ? state.original : this.memberSources.get(id).panel;
      const panel = source.cloneNode(true);
      state.display.replaceWith(panel); state.display = panel; state.selected = id;
      card.toggleAttribute('data-collection-card-views', panel.hasAttribute('data-member-views'));
      for (const choice of state.choices) choice.setAttribute('aria-pressed', String(choice.dataset.collectionColour === id));
      this.updateCardView(card, this.dataset.view);
      this.updateColourOverflow(state);
    }
    updateCardView(card, view) {
      for (const image of card.querySelectorAll('[data-collection-card-image]')) image.hidden = image.dataset.collectionCardImage !== view;
      for (const gallery of card.querySelectorAll('raith-card-gallery')) gallery.setView?.(view);
    }
    syncViewCapability() {
      if (!this.switcher) return;
      const mobile = win.innerWidth <= 900;
      const stream = this.querySelector('.vl-collection__stream');
      const results = stream ? [...stream.children].map(result => result.hasAttribute('data-collection-responsive-result')
        ? result.querySelector(mobile ? '[data-collection-story]' : '.vl-collection__card') : result).filter(Boolean) : [...this.cards, ...this.stories];
      const hasView = results.some(result => result.hasAttribute('data-collection-card-views') || result.hasAttribute('data-collection-story'));
      const complete = results.every(result => !result.hasAttribute('data-collection-story') || result.querySelector('[data-collection-track="product"]'));
      this.switcher.hidden = !hasView || !complete;
    }
    prioritizeResponsiveLead() {
      const results = [...this.querySelectorAll('[data-collection-responsive-result]')];
      for (const result of results) for (const image of result.querySelectorAll('img')) {
        image.setAttribute('loading', 'lazy'); image.setAttribute('fetchpriority', 'auto');
      }
      const first = this.querySelector('.vl-collection__stream')?.firstElementChild;
      if (!first?.hasAttribute('data-collection-responsive-result') || this.querySelector('.vl-collection__cover') || this.dataset.priceSortState === 'pending') return;
      const mobile = win.innerWidth <= 900, view = this.dataset.view || 'model';
      const presentation = first.querySelector(mobile ? '[data-collection-story]' : '.vl-collection__card-media--desktop');
      const image = mobile ? presentation?.querySelector(`[data-collection-track="${view}"] img`) :
        presentation?.querySelector(`[data-collection-card-image="${view}"] img`) || presentation?.querySelector('img');
      if (image) { image.setAttribute('loading', 'eager'); image.setAttribute('fetchpriority', 'high'); }
    }
    setView(requestedView) {
      const view = requestedView === 'product' && this.switcher && !this.switcher.hidden ? 'product' : 'model';
      this.dataset.view = view;
      for (const button of this.querySelectorAll('[data-collection-view]')) {
        button.setAttribute('aria-pressed', String(button.dataset.collectionView === view));
      }
      for (const story of this.stories) {
        for (const track of story.querySelectorAll('[data-collection-track]')) {
          track.hidden = track.dataset.collectionTrack !== view;
          track.scrollLeft = 0;
        }
      }
      for (const card of this.cards) this.updateCardView(card, view);
      this.prioritizeResponsiveLead();
    }
  }
  win.customElements.define('raith-collection', RaithCollection);
}
if (typeof window !== 'undefined' && window.customElements) registerRaithCollection(window);
